import { Button as AriaButton } from '@ariakit/react'
import { clsx } from 'clsx'
import { forwardRef } from 'react'
import { type TrackConfig, trackInteraction } from './tracking'

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'text'
  | 'danger'
  | 'link'
  | 'none'

export interface ButtonProps extends React.ComponentPropsWithoutRef<typeof AriaButton> {
  variant?: ButtonVariant
  children: React.ReactNode
  track?: TrackConfig<React.MouseEvent<HTMLButtonElement>>
}

const BUTTON_BASE_CLASSES =
  'h-10 border-[3px] font-sans font-medium text-base px-4 py-2 whitespace-nowrap no-underline flex gap-2 items-center justify-center leading-none disabled:cursor-not-allowed'

type BorderedButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'text' | 'danger'

const BORDERED_VARIANT_COLORS: Record<BorderedButtonVariant, string> = {
  primary:
    'text-white bg-forest border-forest hover:bg-forest-light hover:border-forest-light disabled:opacity-40',
  // todo
  secondary: 'text-forest bg-transparent border-forest hover:bg-sand disabled:opacity-40',
  // todo
  tertiary:
    'text-charcoal-75 bg-sand border-sand hover:bg-sand-dark hover:border-sand-dark disabled:opacity-40',
  text: 'border-transparent text-forest bg-transparent hover:bg-sand disabled:opacity-40',
  // todo
  danger: 'text-white bg-brick border-brick disabled:opacity-40',
}

export const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary: `${BUTTON_BASE_CLASSES} rounded-lg ${BORDERED_VARIANT_COLORS.primary}`,
  secondary: `${BUTTON_BASE_CLASSES} rounded-lg ${BORDERED_VARIANT_COLORS.secondary}`,
  tertiary: `${BUTTON_BASE_CLASSES} rounded-lg ${BORDERED_VARIANT_COLORS.tertiary}`,
  text: `${BUTTON_BASE_CLASSES} rounded-lg ${BORDERED_VARIANT_COLORS.text}`,
  danger: `${BUTTON_BASE_CLASSES} rounded-lg ${BORDERED_VARIANT_COLORS.danger}`,
  // todo
  link: 'text-indigo underline font-medium disabled:opacity-40 disabled:cursor-not-allowed',
  // todo
  none: 'disabled:opacity-40 disabled:cursor-not-allowed',
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = 'primary', className, children, disabled, onClick, track, ...props }, ref) => {
    const variantClasses = BUTTON_VARIANTS[variant]

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      onClick?.(event)
      if (!disabled) trackInteraction(track, event)
    }

    return (
      <AriaButton
        ref={ref}
        className={clsx(variantClasses, className)}
        disabled={disabled}
        onClick={onClick || track ? handleClick : undefined}
        {...props}
      >
        {children}
      </AriaButton>
    )
  }
)

Button.displayName = 'Button'
